<template>
  <lf-modal
    :close="() => emit('close')"
    :title="$t('EMAIL_INTEGRATION.ADD_NEW_ALIAS')"
    small
  >
    <template #content>
      <loader :is-loading="loading" />
      <div class="divide-y">
        <div class="space-y-2 p-6">
          <span class="text-label">
            {{ $t("COMMON.CHOOSE") }} {{ $t("COMMON.CONTACTS") }}
          </span>
          <search-input
            v-model.trim="searchText"
            no-margin
            no-padding
            data-cy="search-input"
          />
          <div class="max-h-60 space-y-1 overflow-y-auto px-2">
            <lf-checkbox
              v-for="user in filteredUsers"
              :key="user.id"
              v-model="selectedUsersIds"
              :value="user.id"
              :name="user.id.toString()"
              class="w-fit-content"
            >
              <div class="flex flex-col space-y-1">
                <span class="font-semibold text-headline">
                  {{ getFullName(user) }}
                </span>
                <span class="text-label">{{ user.email }}</span>
              </div>
            </lf-checkbox>
          </div>
        </div>
        <modal-buttons
          class="justify-end py-5"
          :on-cancel="() => emit('close')"
          :on-save="handleAliasesUpdate"
          :save-button-text="$t('COMMON.ADD')"
          :disabled="!selectedUsersIds.length"
        />
      </div>
    </template>
  </lf-modal>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import cloneDeep from "lodash/cloneDeep";

import { useClients } from "@/hooks/clients";
import { usePromiseWrapper } from "@/hooks/common";
import clientsApiService from "@/services/modules/clients";
import myApiService from "@/services/modules/my";

import Loader from "@/components/ui/Loader.vue";
import LfModal from "@/components/ui/Modal.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";
import LfCheckbox from "@/components/ui/inputs/LfCheckbox.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";

import type { IWhitelabel } from "@/models/clients";
import type { IUser } from "@/models/users";

const props = defineProps<{
  whitelabel: IWhitelabel;
  selectedUsers: number[];
}>();

const emit = defineEmits<{
  close: [];
  "alias:create": [];
}>();

const { activeClient } = useClients();

const aliasUsers = ref<IUser[]>([]);
const selectedUsersIds = ref<number[]>(cloneDeep(props.selectedUsers));
const searchText = ref("");

const filteredUsers = computed<IUser[]>(() => {
  if (!searchText.value) {
    return aliasUsers.value;
  }

  return aliasUsers.value.filter(({ last_name, first_name, email }) =>
    `${last_name}${first_name}${email}`
      .toLowerCase()
      .includes(searchText.value.toLowerCase())
  );
});

const getFullName = (user: IUser) => {
  return [user.first_name, user.last_name].filter(Boolean).join(" ");
};

const { fetchWrapper: handleAliasesUpdate } = usePromiseWrapper(async () => {
  if (!activeClient.value || !props.whitelabel.id) {
    return;
  }
  await clientsApiService.storeWhitelabelAliases({
    clientId: activeClient.value.id,
    whitelabelId: props.whitelabel.id,
    userIds: selectedUsersIds.value.filter(
      (id) => !props.selectedUsers.includes(id)
    ),
  });
  emit("close");
  emit("alias:create");
});

const { fetchWrapper: getAllAliases, loading } = usePromiseWrapper(async () => {
  if (!activeClient.value || !props.whitelabel.id) {
    return;
  }
  aliasUsers.value = await myApiService.getOrganizationUsers();
});

getAllAliases();
</script>
